/**
 * Scope primitives shared by the provider, the header controls, and the feature
 * pages. A "scope" is the pair (locality, time-range) that every aggregate page
 * reads from the global `ScopeProvider`.
 */

import { thresholds } from "@/lib/thresholds";

/** Time window: a preset number of days, or "all" (whole retained history). */
export type DayRange = number | "all";

// The window numbers below are the server's (`/meta/config` → lib/thresholds),
// read at call time — never module-scope constants that could drift from the API.

/** Presets offered in the header control: the cache-backed windows, plus "all". */
export function dayRangeOptions(): DayRange[] {
  return [...thresholds().window_presets_days, "all"];
}

/** The default window for every scope-driven page. */
export function defaultDays(): DayRange {
  return thresholds().default_analysis_days;
}

/**
 * Upper bound for "all" (and any clamp): the widest window the API accepts, so
 * "all" maps to a bounded query rather than an unbounded scan.
 */
export function maxDays(): number {
  return thresholds().max_analysis_days;
}

/** localStorage key holding the last-used scope so it sticks across navigation. */
export const SCOPE_STORAGE_KEY = "boond.scope.v1";

/** Locality query-param name — the contract shared with the API (see core/scope.py). */
export const LOCALITY_PARAM = "locality";
/**
 * Explicit URL token for the system-wide view. "All localities" must be written
 * to the URL rather than dropping the param: an *absent* param means "inherit the
 * last-used scope", so deleting it would let the previous locality win right back.
 */
export const ALL_LOCALITIES = "all";
/** Time-range query-param name. */
export const DAYS_PARAM = "days";

/** Resolve a `DayRange` to the concrete integer the API expects, clamped to `cap`. */
export function resolveDays(days: DayRange, cap: number = maxDays()): number {
  if (days === "all") return cap;
  return Math.min(days, cap);
}

/** Parse a URL/stored `days` token into a `DayRange` (defaults on garbage). */
export function parseDays(raw: string | null | undefined): DayRange {
  if (raw == null || raw === "") return defaultDays();
  if (raw === "all") return "all";
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : defaultDays();
}

/** System-wide sentinels that all mean "no locality filter" (mirrors the API). */
const SYSTEM_WIDE = new Set(["", "all", "all_localities", "total"]);

/** Normalize a raw locality token to a real name, or `null` for system-wide. */
export function normalizeLocality(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const v = raw.trim();
  return SYSTEM_WIDE.has(v.toLowerCase()) ? null : v;
}
